/**
 * Firestore + staff API for materials price update proposals
 * (materialsPriceProposals / materialsPriceUpdateRequests).
 *
 * Agent proposes; CRM staff Approve (server merge) or Reject via the
 * Materials Database side-by-side review UI.
 */

import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  type Unsubscribe,
  type FirestoreError,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { db, auth, storage } from './firebase';
import { MATERIALS_PRICE_AGENT_ENDPOINT } from './materialsPriceAgentGuide';
import {
  isNoOpMaterialsPriceUpdate,
  omittedUnchangedMaterialsPriceCount,
  visibleMaterialsPriceUpdates,
} from './materialsPriceUpdateDiffs';
import {
  normalizeHardwareUpdateFields,
  resolveProposalCatalogDomain,
  type MaterialsCatalogDomain as DisplayCatalogDomain,
} from './hardwareCatalogDisplay';

export {
  isNoOpMaterialsPriceUpdate,
  omittedUnchangedMaterialsPriceCount,
  visibleMaterialsPriceUpdates,
};
export {
  resolveProposalCatalogDomain,
  normalizeHardwareUpdateFields,
  formatHardwareDiffTitle,
  parseHardwareDiameterLength,
  resolveHardwareDiameterLengthForDisplay,
  isHardwareFastenerCategory,
  resolveHardwareUnitPrice,
} from './hardwareCatalogDisplay';

export type MaterialsPriceProposalStatus = 'pending' | 'approved' | 'rejected';

export type MaterialsCatalogDomain = DisplayCatalogDomain;

export interface MaterialsPriceUpdateDiff {
  matchKey: string;
  action: 'new' | 'update';
  /** Timber fields */
  species?: string;
  grade?: string;
  dimensions?: string;
  moistureContent?: string;
  boardFeet?: number;
  piecePrice?: number;
  costPerBdFt?: number;
  /** Hardware/fastener fields (catalogDomain=hardware) */
  category?: string;
  name?: string;
  description?: string;
  sku?: string;
  unit?: string;
  unitPrice?: number;
  widthInches?: number;
  existingUnitPrice?: number;
  supplier: string;
  existingItemId?: string;
  existingPiecePrice?: number;
  existingCostPerBdFt?: number;
  existingSupplier?: string;
  /** Approve will replace placeholder primary Estimate with this quote's supplier. */
  flipPrimarySupplier?: boolean;
  confidence?: number;
  rationale?: string;
  notes?: string;
}

function normalizeProposalUpdates(raw: unknown): MaterialsPriceUpdateDiff[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const base = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const normalized = normalizeHardwareUpdateFields(base);
    return normalized as unknown as MaterialsPriceUpdateDiff;
  });
}

export interface MaterialsPriceProposal {
  id: string;
  requestId: string;
  submissionId: string;
  catalogDomain?: MaterialsCatalogDomain;
  dealId?: string | null;
  supplierOrgId?: string | null;
  supplierOrgName?: string | null;
  supplierName?: string | null;
  storagePath?: string | null;
  fileName?: string | null;
  downloadUrl?: string | null;
  contentType?: string | null;
  quoteTotal?: number | null;
  quotedSpecies?: string | null;
  rfqPackageId?: string | null;
  rfqProjectId?: string | null;
  status: MaterialsPriceProposalStatus;
  updates: MaterialsPriceUpdateDiff[];
  omittedUnchangedCount?: number;
  parseNotes?: string | null;
  createdAt?: Date;
  reviewedAt?: Date;
  reviewedBy?: string;
}

const PROPOSALS = 'materialsPriceProposals';

/** Composite index needed for preferred query (status + createdAt desc). */
export const MATERIALS_PRICE_PROPOSALS_INDEX_HINT =
  'Firestore composite index on materialsPriceProposals: status ASC, createdAt DESC — https://console.firebase.google.com/project/lctf-projects/firestore/indexes';

function toDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function fromDoc(id: string, d: Record<string, unknown>): MaterialsPriceProposal {
  const updates = normalizeProposalUpdates(d.updates);
  const catalogDomain = resolveProposalCatalogDomain({
    catalogDomain: typeof d.catalogDomain === 'string' ? d.catalogDomain : null,
    supplierName: (d.supplierName as string) || null,
    supplierOrgName: (d.supplierOrgName as string) || null,
    fileName: (d.fileName as string) || null,
    parseNotes: (d.parseNotes as string) || null,
    updates: updates as unknown as Array<Record<string, unknown>>,
  });
  return {
    id,
    requestId: String(d.requestId || d.submissionId || ''),
    submissionId: String(d.submissionId || d.requestId || ''),
    catalogDomain,
    dealId: (d.dealId as string) || null,
    supplierOrgId: (d.supplierOrgId as string) || null,
    supplierOrgName: (d.supplierOrgName as string) || null,
    supplierName: (d.supplierName as string) || null,
    storagePath: (d.storagePath as string) || null,
    fileName: (d.fileName as string) || null,
    downloadUrl: (d.downloadUrl as string) || null,
    contentType: (d.contentType as string) || null,
    quoteTotal: typeof d.quoteTotal === 'number' ? d.quoteTotal : null,
    quotedSpecies: (d.quotedSpecies as string) || null,
    rfqPackageId: (d.rfqPackageId as string) || null,
    rfqProjectId: (d.rfqProjectId as string) || null,
    status: (d.status as MaterialsPriceProposalStatus) || 'pending',
    updates,
    omittedUnchangedCount:
      typeof d.omittedUnchangedCount === 'number' && Number.isFinite(d.omittedUnchangedCount)
        ? d.omittedUnchangedCount
        : 0,
    parseNotes: (d.parseNotes as string) || null,
    createdAt: toDate(d.createdAt),
    reviewedAt: toDate(d.reviewedAt),
    reviewedBy: (d.reviewedBy as string) || undefined,
  };
}

function sortNewestFirst(list: MaterialsPriceProposal[]): MaterialsPriceProposal[] {
  return [...list].sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
}

function isMissingIndexError(err: FirestoreError | Error): boolean {
  const code = 'code' in err ? String(err.code || '') : '';
  const msg = err.message || '';
  return (
    code === 'failed-precondition' ||
    /requires an index|FAILED_PRECONDITION|failed-precondition/i.test(msg)
  );
}

export type MaterialsPriceProposalsListener = (
  list: MaterialsPriceProposal[],
  meta?: { error?: string | null; usedFallback?: boolean },
) => void;

/**
 * Live subscribe to pending materials price proposals (newest first).
 * Prefers status + createdAt orderBy; on missing composite index, falls back to
 * status-only + client-side sort so the review panel stays visible.
 */
export function subscribePendingMaterialsPriceProposals(
  callback: MaterialsPriceProposalsListener,
): Unsubscribe {
  const preferred = query(
    collection(db, PROPOSALS),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc'),
  );

  let fallbackUnsub: Unsubscribe | null = null;
  let cancelled = false;

  const preferredUnsub = onSnapshot(
    preferred,
    snap => {
      callback(
        snap.docs.map(d => fromDoc(d.id, d.data() as Record<string, unknown>)),
        { error: null, usedFallback: false },
      );
    },
    err => {
      console.error('[materialsPriceUpdates] preferred query failed:', err.message, err);
      if (cancelled) return;

      if (isMissingIndexError(err)) {
        console.warn(
          '[materialsPriceUpdates] Missing composite index — falling back to status-only query.',
          MATERIALS_PRICE_PROPOSALS_INDEX_HINT,
        );
        const fallback = query(collection(db, PROPOSALS), where('status', '==', 'pending'));
        fallbackUnsub = onSnapshot(
          fallback,
          snap => {
            callback(
              sortNewestFirst(snap.docs.map(d => fromDoc(d.id, d.data() as Record<string, unknown>))),
              { error: null, usedFallback: true },
            );
          },
          fallbackErr => {
            console.error('[materialsPriceUpdates] fallback query failed:', fallbackErr.message, fallbackErr);
            callback([], {
              error: fallbackErr.message || 'Failed to load price update proposals',
              usedFallback: true,
            });
          },
        );
        return;
      }

      callback([], {
        error: err.message || 'Failed to load price update proposals',
        usedFallback: false,
      });
    },
  );

  return () => {
    cancelled = true;
    preferredUnsub();
    fallbackUnsub?.();
  };
}

async function getStaffIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Sign in required to review materials price updates');
  }
  return user.getIdToken();
}

async function staffAction(
  action:
    | 'applyMaterialsPriceUpdates'
    | 'rejectMaterialsPriceUpdates'
    | 'repairTimberPriceHistory'
    | 'repairGlulamPriceHistory'
    | 'repairHardwarePriceHistory',
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const token = await getStaffIdToken();
  const response = await fetch(MATERIALS_PRICE_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action, ...body }),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok || data?.success === false) {
    throw new Error(
      (typeof data?.error === 'string' && data.error) ||
        `Materials price ${action} failed (HTTP ${response.status})`,
    );
  }
  return data;
}

/** Staff Approve — server-side merge into timber or hardware materials DB. */
export async function approveMaterialsPriceProposal(
  proposalId: string,
  catalogDomain: MaterialsCatalogDomain = 'timber',
): Promise<void> {
  await staffAction('applyMaterialsPriceUpdates', { proposalId });
  // Ensure priceHistory exists even if an older apply path omitted it.
  try {
    if (catalogDomain === 'hardware') {
      await staffAction('repairHardwarePriceHistory', { proposalId });
    } else if (catalogDomain === 'glulam') {
      await staffAction('repairGlulamPriceHistory', { proposalId });
    } else {
      await staffAction('repairTimberPriceHistory', { proposalId });
    }
  } catch (err) {
    console.warn('[materialsPrice] price history repair after approve failed', err);
  }
}

/** Backfill priceHistory from recent approved proposals (Trend sparklines). */
export async function repairRecentTimberPriceHistory(
  limit = 5,
  opts?: { force?: boolean },
): Promise<void> {
  await staffAction('repairTimberPriceHistory', {
    limit,
    ...(opts?.force ? { force: true } : {}),
  });
}

/** Staff Reject — leave catalog unchanged. */
export async function rejectMaterialsPriceProposal(
  proposalId: string,
  reason?: string,
): Promise<void> {
  await staffAction('rejectMaterialsPriceUpdates', { proposalId, reason });
}

/**
 * Resolve a viewable URL for the supplier quote PDF (downloadUrl or Storage path).
 */
export async function resolveMaterialsQuoteUrl(proposal: MaterialsPriceProposal): Promise<string | null> {
  if (proposal.downloadUrl) return proposal.downloadUrl;
  if (!proposal.storagePath) return null;
  try {
    return await getDownloadURL(storageRef(storage, proposal.storagePath));
  } catch {
    return null;
  }
}

/** Format currency for diff display. */
export function formatPriceDiff(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
